import { Container, Divider } from '@mui/material'
import Logo from 'images/logo_rectangle_new.svg'
import React, { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { getProductsDataSheetDownloadUrl, getWholesaleBlogUrl, WHOLESALE_ROUTES } from '../../../../urls'
import { getWholesaleCompanyMenuLinks, SHORT_SITE_NAME, SITE_NAME } from '../../../../../constants'
import { useWholesaleProductsQuery } from '@graphql/queries/__generated__/wholesale.generated'
import { Trans, useTranslation } from 'react-i18next'
import { useMeQuery } from '@graphql/queries/__generated__/me.generated'
import { WholesaleAppStatusEnum } from '../../../../../generated/gqlTypes'
import { useBlogListQuery } from '@graphql/queries/__generated__/blog.generated'

interface IHeaderProps {}

const Footer: React.FC<IHeaderProps> = () => {
  const { t } = useTranslation()
  const { data } = useWholesaleProductsQuery({
    variables: { first: 10 },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  })
  const meQuery = useMeQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  })

  const blogsQueryResult = useBlogListQuery({
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    variables: { first: 1, isWholesale: true },
  })

  const products = useMemo(() => data?.wholesaleProducts.edges.map(edge => edge.node) || [], [data])
  const companyLinks = getWholesaleCompanyMenuLinks(t)
  const blog = blogsQueryResult.data?.blogList.edges.map(edge => edge.node)[0]
  if (blog) {
    companyLinks.push({ url: getWholesaleBlogUrl(blog.slug), title: blog.name })
  }
  const canShowDownloadLinks = useMemo(
    () =>
      meQuery.data?.me?.isStaff ||
      meQuery.data?.me?.wholesaleApp?.status === WholesaleAppStatusEnum.Confirmed,
    [meQuery.data?.me]
  )

  return (
    <footer>
      <Divider />
      <Container className='pt-30 md:pt-60 pb-30 md:pb-100' maxWidth='lg' disableGutters>
        <div className='flex flex-wrap'>
          <div className='w-full md:w-3/5 mb-10 px-10 flex flex-col md:flex-row'>
            <div className='w-full'>
              <div className='mb-20 h-50 max-w-full relative'>
                <Link to={WHOLESALE_ROUTES.HOME} aria-label='home'>
                  <img alt='logo' src={Logo} className='max-h-full' />
                </Link>
              </div>
              <Trans
                i18nKey='wholesale.footerDescription'
                defaults={
                  '<p>{{siteName}} ({{shortSiteName}}) is a team of professional artists that provides high-quality designs for anyone whose hobby is cross-stitching, embroidery, diamond painting and beading.</p>' +
                  '<p>Over 4,000 incredible designs. Whether your customers are beginners or needle-arts variants, our eye-catching designs were created to make crafting interesting and fun. Modern, whimsical, abstract, detailed - we have it all.</p>'
                }
                components={{ p: <p className='leading-relaxed text-lg md:pr-60' /> }}
                values={{ siteName: SITE_NAME, shortSiteName: SHORT_SITE_NAME }}
              />
            </div>
          </div>
          <div className='w-1/2 md:w-1/5 mb-10 px-10'>
            <div className='uppercase text-base font-bold'>{t('download', 'Download')}</div>
            <ul className='m-0 p-0 list-none mt-20'>
              <li>
                <Link
                  className='py-8 text-base no-underline hover:text-indigo-700 block'
                  to={WHOLESALE_ROUTES.INFORMATION}
                  target='_blank'
                  download
                >
                  {t('wholesale.wholesalerInformation', 'Wholesaler Information')}
                </Link>
              </li>
              {canShowDownloadLinks &&
                products
                  .filter(node => node.category?.productsDataFile?.resultFile)
                  .map(node => (
                    <li key={node.id}>
                      <a
                        className='py-8 text-base no-underline hover:text-indigo-700 block'
                        href={getProductsDataSheetDownloadUrl(node.category.productsDataFile.pk)}
                        download
                      >
                        {node.name} {t('dataSheet', 'Data Sheet')}
                      </a>
                    </li>
                  ))}
            </ul>
          </div>
          <div className='w-1/2 md:w-1/5 mb-10 px-10'>
            <div className='uppercase text-base font-bold'>{t('company', 'Company')}</div>
            <ul className='m-0 p-0 list-none mt-20'>
              {companyLinks.map(link => (
                <li key={link.title}>
                  <Link className='py-8 text-base no-underline hover:text-indigo-700 block' to={link.url}>
                    {link.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Container>
    </footer>
  )
}

export default Footer
